import Tippy from '@tippyjs/react';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from 'react-router-dom';
import { eventDB, fetchSession, IClub, IEvent } from '../../db';
import { updatePriceItem } from '../../db/Clients/stripe';
import { gatewayDB, IGateway } from '../../db/Couch/gateway';
import { findResultsByEvent, IResults, resultDB } from '../../db/Couch/result';
import { EMANAGER_ROLES } from '../../hooks';
import { AppRoutes } from '../../router';
import { IRootState } from '../../store';
import { setEvent } from '../../store/eventReducer';
import { getEditPath, getFee } from '../../utils/common-utils';
import { showAlert, toast, useErrorAlerts } from '../Alerts';
import { EditEvent } from '../Modals';

interface IEventRowProps {
  event: IEvent;
  club: IClub;
  fetchEvents: () => void;
  handleCloneEvent: (event: IEvent) => void;
}

export const EventRow: FC<IEventRowProps> = ({ event, club, fetchEvents, handleCloneEvent }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const handleError = useErrorAlerts();
  const { user } = useSelector((state: IRootState) => state.user);
  const { _id, name, dates, featParti, featMatches, featScores, waitlist } = event;

  const startDate = new Date(dates[0]);
  const startDay = startDate.getUTCDate();
  const startMonth = startDate.toLocaleString('default', { month: 'long' });
  const endDate = dates[1] ? new Date(dates[1]).getUTCDate() : null;

  const isClubAdmin = club?.owners?.find((owner) => owner.id === user.metadata?.id)?.role === EMANAGER_ROLES.CLUB_ADMIN;
  const isAdmin = user.roles?.includes('admin');

  const removeEvent = async (event: IEvent) => {
    // Check if there are any results for it.
    // If results present get user confirmation
    // -- if results are not present or empty. Remove it in one go.
    // -- if user ask to keep the result. Then hide the event but keep it in DB.
    // -- TODO: remove any pending requests for the event.
    // -- TODO: remove any pending waitlist for the event.
    try {
      await fetchSession();
      const hideEvent = async (results: IResults) => {
        const latestEvent = await eventDB().get(event._id);
        const hidden = await eventDB().put({
          ...latestEvent,
          hidden: true,
        });
        const hiddenResults = await resultDB().put({
          ...results,
          hidden: true,
        });
        if (hidden.ok && hiddenResults.ok) toast({ title: t('EVENT_HIDDEN'), position: 'top-end', timer: 10000 });
        fetchEvents && fetchEvents();
      };
      const removeEvent = async () => {
        if (event.priceItem) {
          // Remove Stripe Price Item
          gatewayDB<IGateway>().find({
            selector: {
              clubId: event.club,
            },
          }).then(async (data) => {
            const stripeGateway = data.docs.find((doc) => doc.type === 'STRIPE');
            const deactivated = await updatePriceItem(event.priceItem, false, event.name, stripeGateway!.stripeAccountId);
            if (deactivated) {
              toast({ title: 'Old Price Item deactivated', timer: 3000 });
            }

            // TODO: Remove the item from Team Up Mate as well if exist
          });
        }
        const removed = await eventDB().remove(event as any);
        if (removed.ok && fetchEvents) {
          fetchEvents();
          toast({ title: t('EVENT_REMOVED'), position: 'top-end', timer: 10000 });
        }
      };
      const removeScores = async (results: IResults) => {
        const removed = await resultDB().remove(results as any);
        if (removed.ok) removeEvent();
      };
      const checkResults = async () => {
        const results = await findResultsByEvent(event?._id);
        if (results?.length) {
          showAlert({
            icon: 'question',
            titleText: t('CONFIRM_REMOVAL_OF_RESULTS'),
            text: t(`CONFIRM_REMOVAL_OF_RESULTS_BODY`, { link: 'link' }),
            confirmButtonText: t('REMOVE_EVENT'),
            confirmButtonColor: '#e2a13f',
            cancelButtonText: t('CANCEL_REMOVE'),
            cancelButtonColor: '#00ab55',
            showDenyButton: true,
            denyButtonText: t('HIDE_INSTEAD'),
            denyButtonColor: '#4362ee',
            showCancelButton: true,
            allowEscapeKey: true,
            allowOutsideClick: true,
            onConfirm: () => removeScores(results[0]),
            onDenied: () => hideEvent(results[0]),
          });
        } else {
          removeEvent();
        }
      };
      showAlert({
        icon: 'warning',
        titleText: t('ARE_YOU_SURE_TO_REMOVE'),
        text: t(`ARE_YOU_SURE_TO_REMOVE_BODY`, { name }),
        confirmButtonText: t('REMOVE'),
        confirmButtonColor: '#e7515a',
        cancelButtonText: t('CANCEL'),
        showCancelButton: true,
        allowEscapeKey: true,
        allowOutsideClick: true,
        onConfirm: () => checkResults(),
      });
    } catch (e) {
      handleError(e, 'remove_event');
    }
  };

  const disciplineColor = club?.clubDisciplines?.find(dis => dis.name === event.type)?.color || 'primary';

  return <div className={`flex flex-col md:flex-row shaddow-md shadow rounded  ${event.hidden ? 'bg-gray-200/50' : 'bg-white'}`}>
    <div className='pb-0 md:pb-4 p-4 flex'>
      <div className="flex md:flex-col justify-center items-center text-gray-500 md:ps-4">
        <div className="text-2xl md:text-4xl font-bold font-sans me-1 md:me-0">{startDay}</div>
        {endDate && startDay !== endDate && <div className="text-sm whitespace-nowrap md:capitalize me-0.5 md:me-0">{t('TO')} {endDate}</div>}
        <div className="text-md uppercase">{startMonth}</div>
      </div>
    </div>
    <div className='p-4 pt-0 md:p-4'>
      <h2 className="text-lg font-semibold text-gray-700">
        <Link to={`/${_id}`} className='hover:underline'>{name}</Link>
        {event.hidden && <span className="text-xs font-semibold bg-gray-500/50 rounded-md px-2 py-1 ms-2">HIDDEN</span>}
        <div className='flex gap-2'>
          {event.fee && <div className="text-xs font-semibold"><span className='uppercase'>{club.currency}</span> {getFee(event.fee, event.tumItem)}</div>}
          {event.type && <div className={`text-${disciplineColor} text-xs font-semibold`}>
            {event.type}
          </div>}
        </div>
      </h2>
      <div className="mt-auto pt-4">
        <ul className="flex gap-4 flex-wrap">
          <li>
            <EditEvent event={event} fetchEvents={fetchEvents} />
          </li>
          {featParti && (<li>
            <Tippy content={t('BUILD_FORM')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.BUILD_FORM))} type="button" className="text-[#F59E0B] group">
                <i className='bx bxs-file text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30' />
                <div>{t("ENTRY_FORM")}</div>
              </button>
            </Tippy>
          </li>)}
          {featMatches && (<li>
            <Tippy content={t('VIEW_MATCHES')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.MATCHES))} type="button" className="text-[#EC4899] group">
                <i className='bx bxs-medal text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("MATCHES")}</div>
              </button>
            </Tippy>
          </li>)}
          {featScores && (<li>
            <Tippy content={t('SHOW_AGGS')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.AGGS))} type="button" className="text-[#14B8A6] group">
                <i className='bx bxs-trophy text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("AGGS")}</div>
              </button>
            </Tippy>
          </li>)}
          {featParti && (<li>
            <Tippy content={t('CHECK_LIST')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.LIST_PARTICIPANTS))} type="button" className="text-[#10B981] group">
                <i className='bx bxs-user-detail text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("ENTRIES")}</div>
              </button>
            </Tippy>
          </li>)}
          {featParti && waitlist && (<li>
            <Tippy content={t('VIEW_WAITLIST')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.WAITLIST))} type="button" className="text-[#8B5CF6] group">
                <i className='bx bxs-hourglass-bottom text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("WAITLIST")}</div>
              </button>
            </Tippy>
          </li>)}
          {featParti && featMatches && (<li>
            <Tippy content={t('VIEW_SQUADDING')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.SQUADDING))} type="button" className="text-[#6366F1] group">
                <i className='bx bxs-group text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("SQUADDING")}</div>
              </button>
            </Tippy>
          </li>)}
          {featParti && featMatches && featScores && (<li>
            <Tippy content={t('ENTER_SCORES')} theme="info">
              <button onClick={() => (dispatch(setEvent(event)), navigate(AppRoutes.ENTER_SCORE))} type="button" className="text-[#22D3EE] group">
                <i className='bx bxs-book-add text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("SCORES")}</div>
              </button>
            </Tippy>
          </li>)}
          {(isClubAdmin || isAdmin) && (<li>
            <Tippy content={t('CLONE_EVENT')} theme="info">
              <button onClick={() => handleCloneEvent(event)} type="button" className="text-[#EF4444] group">
                <i className='bx bx-copy-alt text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("CLONE")}</div>
              </button>
            </Tippy>
          </li>)}
          {(isClubAdmin || isAdmin) && (<li>
            <Tippy content={t('REMOVE_EVENT')} theme="info">
              <button onClick={() => removeEvent(event)} type="button" className="text-[#F43F5E] group">
                <i className='bx bxs-trash text-xl leading-4 opacity-60 group-hover:opacity-100 transition-opacity duration-30'></i>
                <div>{t("DELETE")}</div>
              </button>
            </Tippy>
          </li>)}
        </ul>
      </div>
    </div>
    {(event.featMatches || event.featParti || event.featScores) && <div className='md:ms-auto flex w-full md:w-auto'>
      <button type="button" onClick={() => (dispatch(setEvent(event)), navigate(getEditPath(event)))} className={`bg-${disciplineColor} text-white p-2 md:p-4 flex flex-row md:flex-col justify-center items-center gap-2 flex-wrap content-center rounded-bl-md w-full md:w-auto md:rounded-bl-none md:rounded-tr-md rounded-br-md`}>
        <i className='bx bxs-check-circle text-xl md:text-3xl block'></i>
        <div className='whitespace-nowrap'>{t("SET_ACTIVE")}</div>
      </button>
    </div>}
  </div>;
}